import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Printer, Package, Wrench, ShieldCheck, GraduationCap, Ticket, Cloud, Building2, Factory, Shuffle, RefreshCw, CreditCard, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Deployment type metadata. Real lucide icons (no emojis) so the UI looks
// consistent with the rest of the CRM aesthetic.
const DEP_BADGE = {
  cloud_saas:     { label: 'SaaS',       Icon: Cloud,     cls: 'bg-sky-100 text-sky-700' },
  on_premise:     { label: 'On-Prem',    Icon: Building2, cls: 'bg-slate-200 text-slate-700' },
  customer_cloud: { label: 'Cust-Cloud', Icon: Factory,   cls: 'bg-indigo-100 text-indigo-700' },
  hybrid:         { label: 'Hybrid',     Icon: Shuffle,   cls: 'bg-purple-100 text-purple-700' },
};

function DepChip({ deployment, sizeClasses = 'text-[10px] px-1.5 py-0.5' }) {
  const dep = typeof deployment === 'string' ? DEP_BADGE[deployment] : deployment;
  if (!dep) return null;
  const Icon = dep.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded font-medium ${sizeClasses} ${dep.cls}`}>
      {Icon && <Icon className="w-3 h-3" />}
      <span>{dep.label}</span>
    </span>
  );
}

const ADDON_ICON = {
  professional_services: Wrench,
  amc: ShieldCheck,
  training: GraduationCap,
  support: ShieldCheck,
  one_time: Ticket,
};

function fmtINR(v) {
  if (!v && v !== 0) return '—';
  return '₹' + Number(v).toLocaleString('en-IN');
}

const BILLING_CYCLE_OPTIONS = [
  { v: 'monthly', label: 'Monthly' },
  { v: 'quarterly', label: 'Quarterly' },
  { v: 'half_yearly', label: 'Half-yearly' },
  { v: 'annually', label: 'Annually' },
  { v: 'one_time', label: 'One-time' },
];

const PAYMENT_TERMS_OPTIONS = [
  { v: 'net_15', label: 'Net 15' },
  { v: 'net_30', label: 'Net 30' },
  { v: 'net_45', label: 'Net 45' },
  { v: 'net_60', label: 'Net 60' },
  { v: 'advance', label: 'Advance' },
];

function cycleLabel(v) { return BILLING_CYCLE_OPTIONS.find(o => o.v === v)?.label; }
function termsLabel(v) { return PAYMENT_TERMS_OPTIONS.find(o => o.v === v)?.label; }

function PricingUnitLabel({ unit }) {
  const map = {
    per_user_per_month: '/user/month',
    per_user_per_year: '/user/year',
    per_agent_per_month: '/agent/month',
    per_minute: '/minute',
    per_transaction: '/transaction',
    flat_license: 'flat',
    one_time: 'one-time',
    custom: 'custom',
  };
  return <span className="text-xs text-slate-500">{map[unit] || unit}</span>;
}

function AddLineItemModal({ opportunityId, onSaved, onClose, existingItems }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [kind, setKind] = useState('sku'); // 'sku' | 'addon'
  const [form, setForm] = useState({
    sku_id: '', addon_id: '',
    quantity: 1, term_months: 12, discount_pct: 0, custom_amount: 0,
    billing_cycle: 'annually', payment_terms: 'net_30', start_date: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data || []));
  }, []);

  useEffect(() => {
    if (!selectedProduct) { setDetail(null); return; }
    axios.get(`${API}/products/${selectedProduct}`).then(r => setDetail(r.data));
  }, [selectedProduct]);

  const skus = (detail?.skus || []).filter(s => s.active);
  const addons = (detail?.addons || []).filter(a => a.active);
  const selectedSku = skus.find(s => s.id === form.sku_id);
  const selectedAddon = addons.find(a => a.id === form.addon_id);
  const isCustomQuote = selectedSku?.commercial_model === 'custom_quote';

  async function save() {
    if (kind === 'sku' && !form.sku_id) { toast.error('Pick a SKU'); return; }
    if (kind === 'addon' && !form.addon_id) { toast.error('Pick an add-on'); return; }
    setSaving(true);
    const newItem = kind === 'sku'
      ? {
          kind: 'sku',
          sku_id: form.sku_id,
          quantity: Number(form.quantity || 0),
          term_months: Number(form.term_months || 0),
          discount_pct: Number(form.discount_pct || 0),
          custom_amount: Number(form.custom_amount || 0),
          billing_cycle: form.billing_cycle || null,
          payment_terms: form.payment_terms || null,
          start_date: form.start_date || null,
        }
      : { kind: 'addon', addon_id: form.addon_id, quantity: Number(form.quantity || 0), discount_pct: Number(form.discount_pct || 0) };
    const payload = { line_items: [...existingItems.map(stripForWrite), newItem] };
    try {
      await axios.put(`${API}/opportunities/${opportunityId}/line-items`, payload);
      toast.success(kind === 'sku' ? 'SKU added' : 'Add-on added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-lineitem-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Add to Deal</h3>
        <div className="flex gap-1 mb-4 border-b border-slate-200 dark:border-slate-700">
          <button onClick={() => setKind('sku')} className={'px-4 py-2 text-sm font-medium border-b-2 ' + (kind === 'sku' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500')} data-testid="li-kind-sku">SKU (License)</button>
          <button onClick={() => setKind('addon')} className={'px-4 py-2 text-sm font-medium border-b-2 ' + (kind === 'addon' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500')} data-testid="li-kind-addon">Add-on (AMC / Services)</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Product</label>
            <select value={selectedProduct || ''} onChange={e => { setSelectedProduct(e.target.value); setForm({ ...form, sku_id: '', addon_id: '' }); }} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-product-select">
              <option value="">— Select a product —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {selectedProduct && kind === 'sku' && (
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">SKU</label>
              <select value={form.sku_id} onChange={e => setForm({...form, sku_id: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-sku-select">
                <option value="">— Select a SKU —</option>
                {skus.map(s => {
                  const dep = DEP_BADGE[s.deployment_type]?.label || s.deployment_type;
                  const priceStr = s.commercial_model === 'custom_quote' ? 'custom quote' : `${fmtINR(s.list_price)}`;
                  return <option key={s.id} value={s.id}>{`${dep} · ${s.tier || '—'} · ${s.sku_code} · ${priceStr}`}</option>;
                })}
              </select>
              {skus.length === 0 && <p className="text-xs text-amber-600 mt-1">No active SKUs on this product. Add one first in the Products page.</p>}
            </div>
          )}

          {selectedProduct && kind === 'addon' && (
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Add-on</label>
              <select value={form.addon_id} onChange={e => setForm({...form, addon_id: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-addon-select">
                <option value="">— Select an add-on —</option>
                {addons.map(a => {
                  const price = a.pricing_model === 'percentage_of_license' ? `${a.percentage}% of license` : `${fmtINR(a.amount)}${a.pricing_model === 'per_day' ? '/day' : a.pricing_model === 'per_hour' ? '/hr' : ''}`;
                  return <option key={a.id} value={a.id}>{`[${a.type}] ${a.name} · ${price}`}</option>;
                })}
              </select>
              {addons.length === 0 && <p className="text-xs text-amber-600 mt-1">No add-ons on this product.</p>}
            </div>
          )}

          {kind === 'sku' && selectedSku && !isCustomQuote && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Quantity (users)</label>
                <input type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-quantity" />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Term (months)</label>
                <input type="number" min="0" value={form.term_months} onChange={e => setForm({...form, term_months: e.target.value})} disabled={['flat_license', 'one_time', 'per_minute', 'per_transaction'].includes(selectedSku.pricing_unit)} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 disabled:opacity-50" data-testid="li-term" />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Discount %</label>
                <input type="number" min="0" max="100" value={form.discount_pct} onChange={e => setForm({...form, discount_pct: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-discount" />
              </div>
            </div>
          )}

          {kind === 'sku' && isCustomQuote && (
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Custom Quote Amount (INR)</label>
              <input type="number" value={form.custom_amount} onChange={e => setForm({...form, custom_amount: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-custom-amount" />
              <p className="text-[11px] text-slate-500 mt-1">Hybrid / custom-quote SKUs use a manual price entered here.</p>
            </div>
          )}

          {kind === 'sku' && selectedSku && (
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Billing Cycle</label>
                <select value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-billing-cycle">
                  {BILLING_CYCLE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Payment Terms</label>
                <select value={form.payment_terms} onChange={e => setForm({...form, payment_terms: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-payment-terms">
                  {PAYMENT_TERMS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Start Date</label>
                <input type="date" value={form.start_date || ''} onChange={e => setForm({...form, start_date: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="li-start-date" />
              </div>
            </div>
          )}


          {kind === 'addon' && selectedAddon && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">{selectedAddon.pricing_model === 'per_day' ? 'Days' : selectedAddon.pricing_model === 'per_hour' ? 'Hours' : 'Quantity'}</label>
                <input type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Discount %</label>
                <input type="number" min="0" max="100" value={form.discount_pct} onChange={e => setForm({...form, discount_pct: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50" data-testid="li-save-btn">{saving ? 'Saving…' : 'Add to Deal'}</button>
        </div>
      </div>
    </div>
  );
}

function stripForWrite(li) {
  // Keep only the fields the backend needs to re-hydrate
  if (li.kind === 'sku') {
    return {
      kind: 'sku',
      sku_id: li.sku_id,
      quantity: li.quantity,
      term_months: li.term_months,
      discount_pct: li.discount_pct,
      custom_amount: li.custom_amount,
      billing_cycle: li.billing_cycle || null,
      payment_terms: li.payment_terms || null,
      start_date: li.start_date || null,
    };
  }
  return { kind: 'addon', addon_id: li.addon_id, quantity: li.quantity, discount_pct: li.discount_pct };
}

export default function OpportunityLineItems({ opportunityId, onTotalChange, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [breakdown, setBreakdown] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/opportunities/${opportunityId}/line-items`)
      .then(r => { setItems(r.data.line_items || []); setBreakdown(r.data.breakdown || {}); onTotalChange?.(r.data.breakdown?.grand_total || 0); })
      .catch(() => toast.error('Failed to load line items'))
      .finally(() => setLoading(false));
  }, [opportunityId, onTotalChange]);

  useEffect(load, [load]);

  async function removeItem(idx) {
    if (readOnly) return;
    if (!window.confirm('Remove this line?')) return;
    const kept = items.filter((_, i) => i !== idx).map(stripForWrite);
    try {
      await axios.put(`${API}/opportunities/${opportunityId}/line-items`, { line_items: kept });
      toast.success('Line removed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  }

  async function openQuote() {
    try {
      const r = await axios.get(`${API}/opportunities/${opportunityId}/quote`);
      renderPrintableQuote(r.data);
    } catch {
      toast.error('Failed to generate quote');
    }
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6" data-testid="opp-lineitems">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Package className="w-5 h-5 text-teal-500" />
          Products & SKUs
          {items.length > 0 && <span className="text-sm font-normal text-slate-400">({items.length})</span>}
        </h2>
        <div className="flex gap-2">
          {items.length > 0 && (
            <button onClick={openQuote} className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-sm hover:bg-slate-200 dark:hover:bg-slate-600" data-testid="opp-print-quote">
              <Printer className="w-4 h-4" /> Quote
            </button>
          )}
          {!readOnly && (
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 px-3 py-1.5 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-lg text-sm hover:bg-teal-200" data-testid="opp-add-lineitem">
              <Plus className="w-4 h-4" /> Add
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div></div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <p className="text-sm">No SKUs or add-ons linked.</p>
          <p className="text-xs mt-1">Click <b>+ Add</b> to link a Product → SKU and attach AMC / Professional Services.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((li, idx) => <LineItemRow key={idx} li={li} onRemove={() => removeItem(idx)} readOnly={readOnly} />)}
        </div>
      )}

      {items.length > 0 && <PriceBreakdown bd={breakdown} />}

      {showAdd && (
        <AddLineItemModal
          opportunityId={opportunityId}
          existingItems={items}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function LineItemRow({ li, onRemove, readOnly = false }) {
  if (li.kind === 'sku') {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-900/30 rounded-lg" data-testid="lineitem-row-sku">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <DepChip deployment={li.deployment_type} />
            {li.sku_tier && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{li.sku_tier}</span>}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 uppercase">{li.commercial_model}</span>
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{li.product_name} <span className="font-mono text-[11px] text-slate-500">· {li.sku_code}</span></p>
          <p className="text-[11px] text-slate-500">{fmtINR(li.unit_price)} <PricingUnitLabel unit={li.pricing_unit} /> · qty {li.quantity}{li.term_months ? ` · ${li.term_months}mo` : ''}{li.discount_pct ? ` · -${li.discount_pct}%` : ''}</p>
          {(li.billing_cycle || li.payment_terms || li.start_date) && (
            <p className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
              {li.billing_cycle && (
                <span className="inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> {cycleLabel(li.billing_cycle) || li.billing_cycle}
                </span>
              )}
              {li.payment_terms && (
                <span className="inline-flex items-center gap-1">
                  <CreditCard className="w-3 h-3" /> {termsLabel(li.payment_terms) || li.payment_terms}
                </span>
              )}
              {li.start_date && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {new Date(li.start_date).toLocaleDateString('en-IN')}
                </span>
              )}
            </p>
          )}
        </div>
        <span className="text-sm font-bold text-slate-900 dark:text-white whitespace-nowrap">{fmtINR(li.line_total)}</span>
        {!readOnly && <button onClick={onRemove} className="p-1 text-slate-400 hover:text-red-600 rounded" data-testid="lineitem-remove"><Trash2 className="w-4 h-4" /></button>}
      </div>
    );
  }
  const Icon = ADDON_ICON[li.addon_type] || Wrench;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/10 rounded-lg" data-testid="lineitem-row-addon">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <Icon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 uppercase">{li.addon_type?.replace('_', ' ')}</span>
          {li.recurring && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Recurring{li.period ? ` · ${li.period}` : ''}</span>}
        </div>
        <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{li.addon_name}</p>
        <p className="text-[11px] text-slate-500">
          {li.pricing_model === 'percentage_of_license' ? `${li.percentage}% of license (${fmtINR(li.amc_basis_amount)})` : `${fmtINR(li.amount)}${li.pricing_model === 'per_day' ? '/day' : li.pricing_model === 'per_hour' ? '/hr' : ''} · qty ${li.quantity || 1}`}
        </p>
      </div>
      <span className="text-sm font-bold text-slate-900 dark:text-white whitespace-nowrap">{fmtINR(li.line_total)}</span>
      {!readOnly && <button onClick={onRemove} className="p-1 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
}

function PriceBreakdown({ bd }) {
  if (!bd || bd.line_count === 0) return null;
  const rows = [
    ['License', bd.license_total],
    ['Setup (one-time)', bd.setup_total],
    ['AMC — Year 1', bd.amc_y1_total],
    ['Professional Services', bd.professional_services_total],
    ['One-time Fees', bd.one_time_total],
    ['Training / Support', bd.training_support_total],
  ].filter(r => r[1] > 0);
  return (
    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="price-breakdown">
      <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Deal Breakdown</h4>
      <div className="space-y-1">
        {rows.map(([label, v]) => (
          <div key={label} className="flex justify-between text-xs">
            <span className="text-slate-600 dark:text-slate-300">{label}</span>
            <span className="font-medium text-slate-800 dark:text-white">{fmtINR(v)}</span>
          </div>
        ))}
        {bd.amc_recurring_annual > 0 && (
          <div className="flex justify-between text-[11px] text-slate-500 italic">
            <span>AMC recurring (Y2+ per year)</span>
            <span>{fmtINR(bd.amc_recurring_annual)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm font-bold pt-2 border-t border-slate-200 dark:border-slate-700 mt-2">
          <span className="text-slate-900 dark:text-white">Grand Total (Y1 TCV)</span>
          <span className="text-green-600 dark:text-green-400">{fmtINR(bd.grand_total)}</span>
        </div>
        {bd.acv > 0 && (
          <div className="flex justify-between text-xs font-semibold pt-1" data-testid="price-breakdown-acv">
            <span className="text-slate-700 dark:text-slate-300">Annual Contract Value (ACV)</span>
            <span className="text-indigo-600 dark:text-indigo-400">{fmtINR(bd.acv)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Opens a print-ready window with the quote content
function renderPrintableQuote(q) {
  const w = window.open('', '_blank', 'width=800,height=900');
  if (!w) { toast.error('Popup blocked — allow popups for this site'); return; }
  const bd = q.breakdown || {};
  const lines = q.line_items || [];
  const rupee = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');
  const rows = lines.map(li => {
    if (li.kind === 'sku') {
      return `<tr>
        <td><b>${li.product_name}</b><br><span class="muted">${li.sku_code || ''} · ${(li.deployment_type || '').replace('_', ' ')} · ${(li.commercial_model || '').toUpperCase()}</span></td>
        <td>${li.quantity || '-'}</td>
        <td>${li.term_months ? li.term_months + ' mo' : '-'}</td>
        <td>${rupee(li.unit_price)}</td>
        <td>${li.discount_pct ? li.discount_pct + '%' : '-'}</td>
        <td class="r"><b>${rupee(li.line_total)}</b></td>
      </tr>${li.setup_fee > 0 ? `<tr><td colspan="5" class="muted">One-time setup fee</td><td class="r">${rupee(li.setup_fee)}</td></tr>` : ''}`;
    }
    const priceStr = li.pricing_model === 'percentage_of_license' ? `${li.percentage}% of license` : `${rupee(li.amount)}${li.pricing_model === 'per_day' ? '/day' : li.pricing_model === 'per_hour' ? '/hr' : ''}`;
    return `<tr>
      <td><b>${li.addon_name}</b><br><span class="muted">${(li.addon_type || '').replace('_', ' ')}${li.recurring ? ' · recurring ' + (li.period || '') : ''}</span></td>
      <td>${li.quantity || '-'}</td>
      <td>-</td>
      <td>${priceStr}</td>
      <td>${li.discount_pct ? li.discount_pct + '%' : '-'}</td>
      <td class="r"><b>${rupee(li.line_total)}</b></td>
    </tr>`;
  }).join('');

  const bdRows = [
    ['License', bd.license_total],
    ['Setup', bd.setup_total],
    ['AMC — Year 1', bd.amc_y1_total],
    ['Professional Services', bd.professional_services_total],
    ['One-time Fees', bd.one_time_total],
    ['Training / Support', bd.training_support_total],
  ].filter(r => r[1] > 0).map(r => `<tr><td>${r[0]}</td><td class="r">${rupee(r[1])}</td></tr>`).join('');

  w.document.write(`<!DOCTYPE html><html><head><title>${q.quote_number}</title>
  <style>
    body { font-family: -apple-system, Segoe UI, sans-serif; color: #222; max-width: 780px; margin: 24px auto; padding: 24px; }
    h1 { margin: 0 0 4px; font-size: 28px; letter-spacing: -0.5px; }
    .muted { color: #888; font-size: 11px; }
    .row { display: flex; justify-content: space-between; gap: 24px; margin-top: 24px; }
    .box { padding: 12px 16px; background: #f7f7f9; border-radius: 8px; flex: 1; }
    table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 12px; }
    th, td { text-align: left; padding: 10px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
    th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; background: #fafafa; }
    td.r, th.r { text-align: right; }
    .totals { margin-top: 16px; width: 320px; margin-left: auto; font-size: 13px; }
    .totals table { width: 100%; }
    .totals tr:last-child td { font-weight: 700; font-size: 16px; color: #0f172a; border-top: 2px solid #333; }
    .terms { margin-top: 24px; font-size: 11px; color: #555; }
    .terms ol { padding-left: 18px; }
    .brand { color: #2563eb; font-weight: 700; font-size: 14px; letter-spacing: 1px; }
    @media print { body { margin: 0; } button { display: none; } }
  </style></head><body>
  <div style="display:flex; justify-content: space-between; align-items: flex-start;">
    <div>
      <div class="brand">intalk.io</div>
      <h1>Quotation</h1>
      <div class="muted">Quote # ${q.quote_number}</div>
    </div>
    <div style="text-align:right; font-size: 12px; color: #666;">
      <div>Issued: ${new Date(q.issued_at).toLocaleDateString('en-IN')}</div>
      <div>Valid until: ${new Date(q.valid_until).toLocaleDateString('en-IN')}</div>
    </div>
  </div>

  <div class="row">
    <div class="box">
      <div class="muted">Bill To</div>
      <div style="font-weight:600">${q.company?.name || q.lead?.organization || '—'}</div>
      ${q.lead ? `<div>${q.lead.name || ''}${q.lead.position ? ' · ' + q.lead.position : ''}</div>
      <div class="muted">${q.lead.email || ''} · ${q.lead.phone || ''}</div>` : ''}
    </div>
    <div class="box">
      <div class="muted">Prepared By</div>
      <div style="font-weight:600">${q.owner?.name || '—'}</div>
      <div class="muted">${q.owner?.email || ''} · ${q.owner?.phone || ''}</div>
    </div>
  </div>

  <table>
    <thead><tr><th>Item</th><th>Qty</th><th>Term</th><th>Unit Price</th><th>Discount</th><th class="r">Line Total</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="totals">
    <table>${bdRows}
      <tr><td>Grand Total (Year 1)</td><td class="r">${rupee(bd.grand_total)}</td></tr>
    </table>
    ${bd.amc_recurring_annual > 0 ? `<div style="text-align:right; font-size:11px; color:#666; margin-top:4px;">+ ${rupee(bd.amc_recurring_annual)} AMC per year from Year 2 onward</div>` : ''}
  </div>

  <div class="terms">
    <b>Terms & Conditions</b>
    <ol>${(q.terms || []).map(t => `<li>${t}</li>`).join('')}</ol>
  </div>

  <div style="text-align:center; margin-top: 32px;">
    <button onclick="window.print()" style="padding:8px 20px; background:#2563eb; color:white; border:0; border-radius:6px; cursor:pointer; font-size:14px;">Print / Save as PDF</button>
  </div>
  </body></html>`);
  w.document.close();
}
